// idb-native — wrapper FINO sobre o IndexedDB NATIVO, atrás do MESMO contrato
// que a lib `idb` servia (openDB/deleteDB → get/put/getAll/delete/transação/
// cursor). Existe por um motivo duro: o piso do produto é Chromium 38 (webOS
// 3.5/2017) e 47 (Tizen 3.0), e a lib `idb@8` faz `new Proxy(...)` dentro do
// `openDB` — `Proxy` é Chr49+. No piso a lib REJEITA no `upgradeneeded`, o
// `_dbPromise` cacheia a rejeição pra sempre e TODO o IndexedDB do app fica
// morto em silêncio. Mas o IndexedDB CRU funciona no piso (nativo desde Chr24,
// inclusive em `file://`), então o caminho certo é usar o nativo SEMPRE — sem
// feature-detect, sem ramo lib-vs-nativo (menos ramo = menos bug).
//
// ⚠️ SEGUNDO landmine, além do Proxy: `IDBObjectStore.getAll`/`getAllKeys` (e
// os equivalentes de `IDBIndex`) são Chr48+ — TAMBÉM acima do piso. A lib `idb`
// chama o `getAll` nativo direto, então mesmo sem o Proxy ela quebraria aqui.
// Por isso este wrapper NÃO usa `getAll` nativo: `getAll`/`getAllKeys`/
// `getAllFromIndex`/`getAllKeysFromIndex` são polyfillados via CURSOR
// (`openCursor`/`openKeyCursor`, Chr23/24). Só a API ≤Chr38 é tocada.
//
// Contrato: os call-sites (catalog-store, local-db, vod-info-store,
// series-info-store, sync/outbox) NÃO mudam — só trocam o import de runtime de
// 'idb' pra './idb-native'. Os TIPOS continuam vindo de 'idb' via `import type`
// (apagados no build → zero Proxy, zero bytes no bundle). Uma única cast na
// fronteira (`openDB`) propaga os tipos idb pra toda a cadeia encadeada; o
// runtime abaixo é 100% nativo.
//
// NADA aqui pode usar API > Chr38: sem `Proxy`, sem `getAll`/`getAllKeys`
// nativos, sem `structuredClone` (Chr98), sem `navigator.storage.estimate`
// (Chr61 — o idb-quota-manager já cuida disso, respeitado). Promises (Chr32) e
// sintaxe ES6+ são transpiladas pelo build legacy (--mode tv).

import type {
  DBSchema,
  IDBPDatabase,
  OpenDBCallbacks,
} from 'idb'

// ===== Promisificação de baixo nível =====

/** Envolve uma IDBRequest numa Promise. O request já foi emitido de forma
 *  SÍNCRONA pelo chamador (é o que mantém a transação ativa em Blink antigo);
 *  aqui só amarramos os callbacks. */
function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IDBRequest falhou'))
  })
}

/** Resolve quando a transação COMITA; rejeita em erro/abort. Criada uma vez por
 *  transação (memoizada em wrapTransaction) pra que os handlers sejam anexados
 *  já na criação — anexar tarde, após a tx completar, deixaria a promise pendurada. */
function txToDone(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('transação falhou'))
    tx.onabort = () => reject(tx.error ?? new Error('transação abortada'))
  })
}

type Query = IDBValidKey | IDBKeyRange | null | undefined

/** getAll polyfillado por cursor (o nativo é Chr48+). Ordem ASC por key —
 *  idêntico ao `getAll` nativo; os call-sites reordenam por conta própria. */
function cursorGetAll<T>(source: IDBObjectStore | IDBIndex, query?: Query): Promise<T[]> {
  return new Promise<T[]>((resolve, reject) => {
    const out: T[] = []
    const req = source.openCursor(query ?? null)
    req.onsuccess = () => {
      const cursor = req.result
      if (cursor) {
        out.push(cursor.value as T)
        cursor.continue()
      } else {
        resolve(out)
      }
    }
    req.onerror = () => reject(req.error ?? new Error('getAll(cursor) falhou'))
  })
}

/** getAllKeys polyfillado por key-cursor (o nativo é Chr48+). Devolve as PRIMARY
 *  keys — inclusive quando a fonte é um índice (`getAllKeysFromIndex` sempre quis
 *  as chaves primárias dos registros que casaram, pra deletar por id). */
function cursorGetAllKeys(source: IDBObjectStore | IDBIndex, query?: Query): Promise<IDBValidKey[]> {
  return new Promise<IDBValidKey[]>((resolve, reject) => {
    const out: IDBValidKey[] = []
    const req = source.openKeyCursor(query ?? null)
    req.onsuccess = () => {
      const cursor = req.result
      if (cursor) {
        out.push(cursor.primaryKey)
        cursor.continue()
      } else {
        resolve(out)
      }
    }
    req.onerror = () => reject(req.error ?? new Error('getAllKeys(cursor) falhou'))
  })
}

// ===== Cursor envolvido (usado direto pelos stores: openCursor + delete/continue) =====

interface NativeCursor {
  readonly value: unknown
  readonly key: IDBValidKey
  readonly primaryKey: IDBValidKey
  delete(): Promise<void>
  continue(key?: IDBValidKey): Promise<NativeCursor | null>
}

/** Cursores em IDB nativo reusam O MESMO request: cada `nativeCursor.continue()`
 *  re-dispara o `onsuccess` do request original com o próximo valor (ou null no
 *  fim). Modelamos isso re-armando o handler do request a cada `.continue()`. */
function makeCursor(
  nativeCursor: IDBCursorWithValue,
  req: IDBRequest<IDBCursorWithValue | null>,
): NativeCursor {
  return {
    get value() {
      return nativeCursor.value
    },
    get key() {
      return nativeCursor.key
    },
    get primaryKey() {
      return nativeCursor.primaryKey
    },
    delete() {
      // `delete()` é um request SEPARADO na mesma transação — não interfere no
      // request do cursor. Continuar após deletar é válido no spec (e em
      // fake-indexeddb); os stores que iteram-deletando contam com isso.
      return reqToPromise(nativeCursor.delete())
    },
    continue(key?: IDBValidKey) {
      const next = new Promise<NativeCursor | null>((resolve, reject) => {
        req.onsuccess = () => {
          const c = req.result
          resolve(c ? makeCursor(c, req) : null)
        }
        req.onerror = () => reject(req.error ?? new Error('cursor.continue falhou'))
      })
      // Handler re-armado ANTES de avançar (o executor do Promise roda síncrono).
      if (key === undefined) nativeCursor.continue()
      else nativeCursor.continue(key)
      return next
    },
  }
}

function openCursor(
  source: IDBObjectStore | IDBIndex,
  query?: Query,
  direction?: IDBCursorDirection,
): Promise<NativeCursor | null> {
  return new Promise<NativeCursor | null>((resolve, reject) => {
    const req = source.openCursor(query ?? null, direction)
    req.onsuccess = () => {
      const c = req.result
      resolve(c ? makeCursor(c, req) : null)
    }
    req.onerror = () => reject(req.error ?? new Error('openCursor falhou'))
  })
}

// ===== Índice envolvido =====

function wrapIndex(index: IDBIndex) {
  return {
    get(key: IDBValidKey | IDBKeyRange) {
      return reqToPromise(index.get(key))
    },
    getAll(query?: Query) {
      return cursorGetAll(index, query)
    },
    getAllKeys(query?: Query) {
      return cursorGetAllKeys(index, query)
    },
    count(query?: Query) {
      return reqToPromise(index.count(query ?? undefined))
    },
    openCursor(query?: Query, direction?: IDBCursorDirection) {
      return openCursor(index, query, direction)
    },
  }
}

// ===== Object store envolvido =====

function wrapStore(store: IDBObjectStore) {
  return {
    get name() {
      return store.name
    },
    get indexNames() {
      return store.indexNames
    },
    get(key: IDBValidKey | IDBKeyRange) {
      return reqToPromise(store.get(key))
    },
    getAll(query?: Query) {
      return cursorGetAll(store, query)
    },
    getAllKeys(query?: Query) {
      return cursorGetAllKeys(store, query)
    },
    count(query?: Query) {
      return reqToPromise(store.count(query ?? undefined))
    },
    // put/delete/clear são `async` de propósito: qualquer throw SÍNCRONO do
    // nativo (ex.: o mock de cota da suíte lança de dentro do put) vira rejeição,
    // igual ao comportamento da lib `idb`. Sem isso, um `void store.put().catch()`
    // (fire-and-forget do touch de LRU) explodiria como throw síncrono e mataria
    // o `get` que o disparou.
    async put(value: unknown, key?: IDBValidKey) {
      return reqToPromise(key === undefined ? store.put(value) : store.put(value, key))
    },
    async delete(key: IDBValidKey | IDBKeyRange) {
      return reqToPromise(store.delete(key))
    },
    async clear() {
      return reqToPromise(store.clear())
    },
    index(name: string) {
      return wrapIndex(store.index(name))
    },
    openCursor(query?: Query, direction?: IDBCursorDirection) {
      return openCursor(store, query, direction)
    },
    // Só no upgrade. Devolve o índice nativo (o retorno não é consumido pelos
    // call-sites; é o `createObjectStore(...).createIndex(...)` encadeado).
    createIndex(name: string, keyPath: string | string[], options?: IDBIndexParameters) {
      return store.createIndex(name, keyPath, options)
    },
  }
}

// ===== Transação envolvida =====

function wrapTransaction(tx: IDBTransaction) {
  const done = txToDone(tx)
  return {
    get done() {
      return done
    },
    objectStore(name: string) {
      return wrapStore(tx.objectStore(name))
    },
    /** Atalho de transação de UMA store só (espelha `tx.store` da lib `idb`). */
    get store() {
      const name = tx.objectStoreNames[0]
      if (name === undefined) throw new Error('tx.store: transação sem stores')
      return wrapStore(tx.objectStore(name))
    },
    abort() {
      tx.abort()
    },
  }
}

// ===== Database envolvido =====

/** Escritas de conveniência combinam o request com `tx.done` (igual à lib `idb`):
 *  só resolvem quando a transação COMITA, e a cota (que chega no request.onerror
 *  E no tx abort) sobe como rejeição por qualquer um dos dois. Promise.all já
 *  anexa reação aos dois → sem unhandledrejection quando um rejeita. */
function wrapDatabase(db: IDBDatabase) {
  return {
    get name() {
      return db.name
    },
    get version() {
      return db.version
    },
    get objectStoreNames() {
      return db.objectStoreNames
    },
    close() {
      db.close()
    },
    transaction(storeNames: string | string[], mode?: IDBTransactionMode) {
      return wrapTransaction(db.transaction(storeNames, mode))
    },
    // Upgrade (chamados dentro do onupgradeneeded, síncronos).
    createObjectStore(name: string, options?: IDBObjectStoreParameters) {
      return wrapStore(db.createObjectStore(name, options))
    },
    deleteObjectStore(name: string) {
      db.deleteObjectStore(name)
    },
    // ===== Conveniência: leitura (só o request; sem esperar o commit) =====
    async get(storeName: string, key: IDBValidKey | IDBKeyRange) {
      return reqToPromise(db.transaction(storeName, 'readonly').objectStore(storeName).get(key))
    },
    async getAll(storeName: string, query?: Query) {
      return cursorGetAll(db.transaction(storeName, 'readonly').objectStore(storeName), query)
    },
    async getAllFromIndex(storeName: string, indexName: string, query?: Query) {
      return cursorGetAll(
        db.transaction(storeName, 'readonly').objectStore(storeName).index(indexName),
        query,
      )
    },
    async getAllKeysFromIndex(storeName: string, indexName: string, query?: Query) {
      return cursorGetAllKeys(
        db.transaction(storeName, 'readonly').objectStore(storeName).index(indexName),
        query,
      )
    },
    async count(storeName: string, query?: Query) {
      return reqToPromise(
        db.transaction(storeName, 'readonly').objectStore(storeName).count(query ?? undefined),
      )
    },
    // ===== Conveniência: escrita (request + tx.done, pra durabilidade + cota) =====
    async put(storeName: string, value: unknown, key?: IDBValidKey) {
      const tx = db.transaction(storeName, 'readwrite')
      const store = tx.objectStore(storeName)
      const req = key === undefined ? store.put(value) : store.put(value, key)
      const [result] = await Promise.all([reqToPromise(req), txToDone(tx)])
      return result
    },
    async delete(storeName: string, key: IDBValidKey | IDBKeyRange) {
      const tx = db.transaction(storeName, 'readwrite')
      await Promise.all([reqToPromise(tx.objectStore(storeName).delete(key)), txToDone(tx)])
    },
    async clear(storeName: string) {
      const tx = db.transaction(storeName, 'readwrite')
      await Promise.all([reqToPromise(tx.objectStore(storeName).clear()), txToDone(tx)])
    },
  }
}

// ===== API pública (mesma assinatura da lib `idb`, subset usado) =====

/**
 * Abre (e migra) um IndexedDB usando SÓ a API nativa. Assinatura compatível com
 * `openDB` da lib `idb`: os call-sites passam `openDB<Schema>(nome, versao, {
 * upgrade })`. A única cast (`as unknown as`) na fronteira faz o compilador
 * enxergar os tipos idb em toda a cadeia; o runtime é o wrapper nativo acima.
 */
export function openDB<DBTypes extends DBSchema | unknown = unknown>(
  name: string,
  version: number,
  callbacks: OpenDBCallbacks<DBTypes> = {},
): Promise<IDBPDatabase<DBTypes>> {
  return new Promise<IDBPDatabase<DBTypes>>((resolve, reject) => {
    // `indexedDB.open` pode LANÇAR síncrono (a suíte mocka isso pra modelar o
    // piso "sem IDB"); o executor do Promise captura e rejeita → o abridor
    // (idb-opener.ts) solta a promise e cada método cai no seu catch (fallback
    // de rede).
    const request = indexedDB.open(name, version)
    request.onupgradeneeded = (event) => {
      const nativeDb = request.result
      const nativeTx = request.transaction
      const upgrade = callbacks.upgrade
      if (!upgrade || !nativeTx) return
      // O callback de upgrade dos call-sites é SÍNCRONO (create/deleteObjectStore,
      // createIndex, tx.objectStore().clear()) — roda dentro do onupgradeneeded.
      upgrade(
        wrapDatabase(nativeDb) as unknown as IDBPDatabase<DBTypes>,
        event.oldVersion,
        event.newVersion,
        wrapTransaction(nativeTx) as unknown as Parameters<NonNullable<OpenDBCallbacks<DBTypes>['upgrade']>>[3],
        event,
      )
    }
    request.onsuccess = () => {
      const nativeDb = request.result
      // Mesmos nomes e semântica da lib `idb`:
      //  • `blocking`: ESTA conexão está impedindo outra de apagar ou migrar o
      //    banco (evento `versionchange`). Quem não fecha aqui pendura a outra —
      //    sem prazo. Na TV não acontece (um documento só); na web com várias
      //    abas, acontece.
      //  • `terminated`: o navegador fechou a conexão por conta própria (storage
      //    limpo, erro de disco). Quem guarda a conexão tem que soltá-la.
      // Sem callback, nada muda: o comportamento anterior é preservado.
      if (callbacks.blocking) {
        const blocking = callbacks.blocking
        nativeDb.onversionchange = (event) => {
          blocking(event.oldVersion, event.newVersion, event)
        }
      }
      if (callbacks.terminated) {
        const terminated = callbacks.terminated
        nativeDb.onclose = () => terminated()
      }
      resolve(wrapDatabase(nativeDb) as unknown as IDBPDatabase<DBTypes>)
    }
    request.onerror = () => reject(request.error ?? new Error('openDB falhou'))
    // Sem `onblocked` (o lado de QUEM abre/apaga): os bancos do molde
    // (idb-opener.ts) fecham a própria conexão no `blocking` acima, e o
    // local-db fecha antes de apagar (hardResetLocalDb).
  })
}

/** Apaga um database inteiro. Compat com `deleteDB` da lib `idb` (subset). */
export function deleteDB(name: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error ?? new Error('deleteDB falhou'))
  })
}
